import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import SEO, { serviceSchema } from '../../components/SEO';
import {
  ArrowRight,
  Sparkles,
  Trophy,
  Flame,
  Heart,
  Users,
  Share2,
  QrCode,
  Ticket,
  Zap,
  ScanFace,
  Star,
  ChevronRight,
  Instagram,
  MessageCircle,
  Send,
  Music2,
  Award,
  Radio,
  Camera,
  Volleyball,
  Dumbbell,
  Gamepad2,
  Bike,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import StatsDashboard from '../../components/StatsDashboard';
import { phoneMockups, heroMosaic, fanImages } from '../../mock';
import { getLangFromPath, localizePath } from '../../i18n';

/* Phone frame reused */
function PhoneFrame({ children }) {
  return (
    <div className="relative mx-auto w-[220px] md:w-[240px]">
      <div className="relative aspect-[9/19] rounded-[2.5rem] bg-gradient-to-b from-[#1a1a24] to-[#0a0a0f] p-2 border border-white/10 shadow-2xl shadow-black/70">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-b-2xl z-30" />
        <div className="relative w-full h-full rounded-[2rem] overflow-hidden bg-[#0a0a0f]">{children}</div>
      </div>
      <div className="absolute -inset-6 -z-10 bg-cyan-500/12 blur-3xl rounded-full" />
    </div>
  );
}

/* Screen 1 : Stadium selfie search */
function ScreenStadium() {
  const { t } = useTranslation();
  return (
    <>
      <img src={fanImages.teamColors} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/40" />

      {/* Match badge */}
      <div className="absolute top-10 left-3 right-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-slate-900 text-[10px] font-black shadow">
          LP
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold text-white leading-tight">{t('mockups.fan.eventTitle')}</div>
          <div className="text-[8px] text-white/70 leading-tight">{t('mockups.fan.eventLoc')}</div>
        </div>
        <div className="text-[8px] font-medium text-emerald-300">15K fotos</div>
      </div>

      {/* Face frame */}
      <div className="absolute top-[34%] left-1/2 -translate-x-1/2 w-[52%] aspect-square">
        <span className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-white rounded-tl-md" />
        <span className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-white rounded-tr-md" />
        <span className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-white rounded-bl-md" />
        <span className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-white rounded-br-md" />
      </div>

      <div className="absolute bottom-6 left-3 right-3 text-center">
        <div className="text-[10px] font-semibold text-white mb-1">{t('mockups.fan.lookingForPhotos')}</div>
        <div className="text-[8px] text-white/70 mb-2">{t('mockups.fan.selfieHint')}</div>
        <div className="w-11 h-11 rounded-full bg-white mx-auto" />
      </div>
    </>
  );
}

/* Screen 2 : Fan album with sponsor */
function ScreenFanAlbum() {
  const { t } = useTranslation();
  return (
    <>
      <div className="absolute inset-0 bg-[#0a0a0f]" />

      <div className="absolute top-10 left-0 right-0 px-3">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-slate-900 text-[9px] font-black">LP</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-white leading-tight">{t('mockups.fan.albumTitle')}</div>
            <div className="text-[8px] text-white/50 leading-tight">{t('mockups.fan.userLabel')}</div>
          </div>
          <Heart className="w-4 h-4 text-rose-500 fill-current" />
        </div>

        {/* Score banner */}
        <div className="rounded-lg bg-gradient-to-r from-sky-500 to-blue-600 p-1.5 flex items-center justify-between text-white">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded-full bg-yellow-400 flex items-center justify-center text-slate-900 text-[7px] font-black">A</div>
            <span className="text-[9px] font-bold">ARG</span>
            <span className="text-[11px] font-black">28</span>
          </div>
          <div className="text-[7px] uppercase tracking-wider opacity-80">{t('mockups.fan.fullTime')}</div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] font-black">15</span>
            <span className="text-[9px] font-bold">SCO</span>
            <div className="w-4 h-4 rounded-full bg-white flex items-center justify-center text-blue-800 text-[7px] font-black">S</div>
          </div>
        </div>
      </div>

      <div className="absolute top-[36%] left-3 right-3">
        <div className="rounded-lg overflow-hidden aspect-[4/3] border border-white/8 relative">
          <img src={fanImages.athlete} alt="" className="w-full h-full object-cover" />
          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-red-600 text-white text-[7px] font-black italic">Imperial</div>
        </div>
      </div>

      <div className="absolute top-[63%] left-3 right-3">
        <div className="grid grid-cols-4 gap-1 mb-2">
          {[fanImages.stadiumFans, fanImages.teamColors, fanImages.runner, fanImages.finisher].map((s, i) => (
            <div key={i} className="aspect-square rounded overflow-hidden relative">
              <img src={s} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        <div className="text-[7px] uppercase tracking-wider text-white/40 mb-1">{t('mockups.fan.sponsorsRow')}</div>
        <div className="flex items-center gap-1">
          {[
            { name: 'Visa', bg: 'bg-blue-700' },
            { name: 'Imperial', bg: 'bg-red-600' },
            { name: 'ADIDAS', bg: 'bg-black border border-white/30' },
          ].map((s) => (
            <div key={s.name} className={`flex-1 h-5 rounded ${s.bg} flex items-center justify-center`}>
              <span className="text-[7px] font-bold text-white italic">{s.name}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* Screen 3 : Share with celebration */
function ScreenShareFan() {
  const { t } = useTranslation();
  const contacts = [
    { name: 'Nico', img: fanImages.stadiumFans, social: 'wa' },
    { name: 'Julia', img: fanImages.teamColors, social: 'ig' },
    { name: 'Papá', img: fanImages.finisher, social: 'wa' },
    { name: 'Team', img: fanImages.runner, social: 'tt' },
  ];
  const iconOf = {
    wa: { Icon: MessageCircle, bg: 'bg-emerald-500' },
    ig: { Icon: Instagram, bg: 'bg-gradient-to-br from-fuchsia-500 to-orange-500' },
    tt: { Icon: Music2, bg: 'bg-black border border-white/40' },
  };
  return (
    <>
      <img src={fanImages.athlete} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/50" />

      <div className="absolute top-12 left-3 right-3 flex items-center justify-between">
        <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-400 text-slate-900 text-[9px] font-black">
          <Trophy className="w-3 h-3" /> GANAMOS
        </div>
        <div className="text-[8px] text-white/80">{t('mockups.fan.myAlbum')}</div>
      </div>

      <div className="absolute left-3 right-3 top-[42%] rounded-2xl bg-white/95 backdrop-blur-md p-3 shadow-xl">
        <div className="text-[8px] uppercase tracking-wider text-black/50 mb-2 font-semibold">{t('mockups.fan.shareWith')}</div>
        <div className="flex items-center justify-between gap-1">
          {contacts.map((c) => {
            const { Icon, bg } = iconOf[c.social];
            return (
              <div key={c.name} className="flex flex-col items-center gap-1">
                <div className="relative">
                  <div className="w-9 h-9 rounded-full overflow-hidden border border-black/10">
                    <img src={c.img} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ${bg} flex items-center justify-center shadow`}>
                    <Icon className="w-2.5 h-2.5 text-white" />
                  </div>
                </div>
                <div className="text-[8px] text-black font-medium">{c.name}</div>
              </div>
            );
          })}
          <div className="flex flex-col items-center gap-1">
            <div className="w-9 h-9 rounded-full bg-black/5 flex items-center justify-center">
              <ChevronRight className="w-4 h-4 text-black" />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-r from-sky-500 to-blue-600 flex items-center justify-center gap-2">
        <span className="text-white text-[9px] font-black italic">#VamosPumas</span>
        <span className="text-white/60 text-[8px]">·</span>
        <span className="text-white text-[9px] font-black italic">Visa</span>
      </div>
    </>
  );
}

/* Match Event Card - like Argentina vs Scotland */
function MatchEventCard() {
  const { t } = useTranslation();
  return (
    <div className="relative w-full max-w-[440px] mx-auto">
      {/* Big fan photo behind */}
      <div className="relative w-full aspect-[3/4] rounded-3xl overflow-hidden border border-white/10">
        <img src={fanImages.teamColors} alt="fan" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />

        {/* momentIA badge floating top */}
        <div className="absolute top-6 left-1/2 -translate-x-1/2 w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-display font-black text-2xl shadow-2xl shadow-violet-500/40 border-4 border-[#0a0a0f]">
          m
        </div>

        {/* Match card - bottom overlay */}
        <div className="absolute bottom-4 left-4 right-4 rounded-2xl bg-[#0a0a0f]/85 backdrop-blur-md border border-white/10 overflow-hidden">
          {/* Stadium bg strip */}
          <div className="relative h-24 overflow-hidden bg-[#111116]">
            <img src={fanImages.stadiumFans} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f] via-transparent to-[#0a0a0f]" />
            <div className="relative h-full flex items-center justify-center gap-6 px-4">
              {/* Team 1 logo */}
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-yellow-400 flex items-center justify-center text-slate-900 font-black text-lg shadow-lg">
                  🐆
                </div>
                <div className="text-[9px] font-black text-yellow-300 mt-1 tracking-wider">LOS PUMAS</div>
              </div>
              <div className="text-white/70 font-display font-bold text-lg">vs</div>
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center text-blue-800 font-black text-lg shadow-lg">
                  🏴
                </div>
                <div className="text-[9px] font-black text-white mt-1 tracking-wider">SCOTLAND</div>
              </div>
            </div>
          </div>

          <div className="p-4 pt-3">
            <div className="text-sm font-display font-bold text-white leading-tight">{t('mockups.fan.matchLabel')}</div>
            <div className="text-xs text-white/60 mt-0.5">{t('mockups.fan.matchVenue')}</div>
            <div className="text-xs text-white/40 mt-1">04 de julio de 2026</div>
            <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
              <div className="text-[11px] text-emerald-300 font-semibold flex items-center gap-1">
                <Camera className="w-3 h-3" /> +15 mil fotos
              </div>
              <div className="px-2.5 py-1 rounded-full bg-white text-slate-900 text-[10px] font-bold flex items-center gap-1">
                Buscar mis fotos <ArrowRight className="w-3 h-3" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Sponsor gallery */
function SponsorGallery() {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-3">
      {[
        { src: fanImages.teamColors, tag: 'Camiseta con sponsor', sponsor: 'Visa' },
        { src: fanImages.stadiumFans, tag: 'Cartel del estadio', sponsor: 'Imperial' },
        { src: fanImages.cyclist, tag: 'Vaso oficial', sponsor: 'Coca-Cola' },
        { src: fanImages.athlete, tag: 'Merch del hincha', sponsor: 'ADIDAS' },
      ].map((p, i) => (
        <div key={i} className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 group">
          <img src={p.src} alt="" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
          <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-white/90 backdrop-blur-md text-[9px] font-bold text-slate-900">
            {p.sponsor}
          </div>
          <div className="absolute bottom-3 left-3 right-3">
            <div className="text-[10px] uppercase tracking-wider text-white/60 mb-0.5">{t('mockups.fan.placementNatural')}</div>
            <div className="text-sm font-semibold text-white">{p.tag}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* Sponsor tiers for sports */
function FanSponsorTiers() {
  const { t } = useTranslation();
  const tiers = [
    { name: t('mockups.sports.tierPlatinum'), badge: 'from-slate-100 to-slate-300 text-slate-900', logos: ['Visa'], size: 'h-16' },
    { name: t('mockups.sports.tierGold'), badge: 'from-amber-300 to-amber-500 text-amber-900', logos: ['Imperial', 'ADIDAS'], size: 'h-12' },
    { name: t('mockups.sports.tierSilver'), badge: 'from-zinc-300 to-zinc-400 text-zinc-900', logos: ['Coca-Cola', 'YPF', 'Personal'], size: 'h-10' },
    { name: t('mockups.sports.tierBronze'), badge: 'from-amber-700 to-amber-900 text-amber-50', logos: ['Belgrano', 'Movistar', 'Rappi', 'Aerolíneas'], size: 'h-8' },
  ];
  return (
    <div className="space-y-4">
      {tiers.map((tier) => (
        <div key={tier.name} className="flex items-center gap-4">
          <div className="w-20 shrink-0 text-right">
            <div className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${tier.badge} text-[10px] font-black uppercase tracking-wider`}>{tier.name}</div>
          </div>
          <div className="flex-1 flex flex-wrap gap-2">
            {tier.logos.map((l) => (
              <div key={l} className={`${tier.size} px-4 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center min-w-[100px]`}>
                <span className="text-white/85 font-display font-bold text-sm">{l}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* Fan email templates */
function FanEmailTemplatesMockup() {
  const { t } = useTranslation();
  return (
    <div className="relative">
      {/* Card 1 - Cupón */}
      <div className="rounded-2xl bg-white shadow-2xl p-4 max-w-[300px] relative z-10">
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-black/8">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-slate-900 font-black text-[10px]">LP</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-black leading-tight">{t('mockups.fan.emailTitleThanks')}</div>
            <div className="text-[8px] text-black/50">{t('mockups.fan.emailTo1')}</div>
          </div>
        </div>
        <div className="text-[11px] font-black text-black leading-tight mb-2">{t('mockups.fan.emailSubjectThanks')}</div>
        <div className="w-full aspect-[16/10] rounded-lg overflow-hidden mb-2">
          <img src={fanImages.visaFan} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 p-2.5 text-white">
          <div className="text-[8px] uppercase tracking-wider opacity-80">{t('mockups.fan.exclusiveCoupon')}</div>
          <div className="text-sm font-black leading-tight">{t('mockups.fan.couponBodyThanks')}</div>
          <div className="text-[9px] mt-1 opacity-90">{t('mockups.fan.couponHint')}</div>
        </div>
      </div>

      {/* Card 2 - Agradecimiento del club */}
      <div className="rounded-2xl bg-white shadow-2xl p-4 max-w-[280px] absolute top-16 right-0 rotate-3 z-20">
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-black/8">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-slate-900">
            <Trophy className="w-3.5 h-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-black leading-tight">{t('mockups.fan.emailTitleAssoc')}</div>
            <div className="text-[8px] text-black/50">{t('mockups.fan.emailTo2')}</div>
          </div>
        </div>
        <div className="text-[11px] font-black text-black leading-tight mb-2">{t('mockups.fan.emailSubjectAssoc')}</div>
        <div className="text-[9px] text-black/70 leading-relaxed mb-2">
          {t('mockups.fan.emailBodyAssoc')}
        </div>
        <div className="grid grid-cols-3 gap-1 mb-2">
          {[fanImages.paintedFan, fanImages.heroFan, fanImages.visaFan].map((s, i) => (
            <div key={i} className="aspect-square rounded overflow-hidden">
              <img src={s} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        <div className="h-6 rounded bg-yellow-400 flex items-center justify-center text-slate-900 text-[9px] font-black">
          {t('mockups.fan.viewMyAlbum')}
        </div>
      </div>

      {/* Card 3 - Save the date */}
      <div className="rounded-2xl bg-gradient-to-br from-sky-600 to-indigo-700 text-white shadow-2xl p-4 max-w-[260px] absolute bottom-0 left-8 -rotate-2 z-0">
        <div className="text-[9px] uppercase tracking-wider opacity-80 mb-1">{t('mockups.fan.nextMatchEyebrow')}</div>
        <div className="text-sm font-black leading-tight mb-2">{t('mockups.fan.nextMatchTitle')}</div>
        <div className="text-[9px] opacity-90 leading-relaxed mb-3">
          {t('mockups.fan.nextMatchBody')}
        </div>
        <div className="h-6 rounded bg-white text-blue-700 flex items-center justify-center text-[9px] font-bold">
          {t('mockups.fan.reserveTicket')}
        </div>
      </div>
    </div>
  );
}


function FanStarAlbum() {
  return (
    <div className="relative w-full max-w-[440px] mx-auto">
      {/* Team badge floating */}
      <div className="absolute -top-4 -left-2 z-20 flex items-center gap-3">
        <div className="relative w-14 h-14">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 p-[2px]">
            <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center text-[9px] font-black text-yellow-400">
              LP
            </div>
          </div>
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-blue-500 border-2 border-[#0a0a0f] flex items-center justify-center text-white text-[8px]">
            ✓
          </div>
        </div>
        <div className="hidden sm:block h-3 flex-1 rounded-full bg-white/10 w-40" />
      </div>

      <div className="grid grid-cols-3 gap-2 mt-8">
        <div className="col-span-2 row-span-2 aspect-square rounded-2xl overflow-hidden border border-white/10">
          <img src={fanImages.teamColors} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
          <img src={fanImages.stadiumFans} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
          <img src={fanImages.athlete} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
          <img src={fanImages.runner} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="aspect-square rounded-2xl overflow-hidden border border-yellow-400/40 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 flex items-center justify-center relative">
          <img src={fanImages.cyclist} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="relative text-white font-display font-bold text-lg">+18</div>
        </div>
      </div>

      {/* Floating actions bar */}
      <div className="absolute bottom-[-20px] left-1/2 -translate-x-1/2 flex items-center gap-2 bg-[#0a0a0f]/90 backdrop-blur-md border border-white/10 rounded-full p-1.5 shadow-2xl">
        {[
          { Icon: Heart, bg: 'bg-rose-500' },
          { Icon: Share2, bg: 'bg-blue-500' },
          { Icon: Sparkles, bg: 'bg-gradient-to-br from-violet-400 to-fuchsia-500' },
          { Icon: Trophy, bg: 'bg-gradient-to-br from-yellow-400 to-amber-500' },
        ].map(({ Icon, bg }, i) => (
          <div key={i} className={`w-9 h-9 rounded-full ${bg} flex items-center justify-center text-white`}>
            <Icon className="w-4 h-4" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* Merch checkout mockup */
function MerchCheckoutMockup() {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl bg-white text-black p-4 shadow-2xl max-w-[320px] mx-auto">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-semibold text-black">{t('mockups.fan.clubStore')}</div>
        <div className="text-[9px] text-black/50">{t('mockups.fan.close')}</div>
      </div>
      <div className="rounded-xl bg-slate-100 aspect-video overflow-hidden mb-3 relative">
        <img src={fanImages.teamColors} alt="" className="w-full h-full object-cover" />
        <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded bg-yellow-400 text-slate-900 text-[9px] font-black">
          LOS PUMAS 26'
        </div>
      </div>
      <div className="mb-3">
        <div className="text-[11px] font-black text-black leading-tight">{t('mockups.fan.printTitle')}</div>
        <div className="text-[9px] text-black/50">{t('mockups.fan.printSub')}</div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-lg border border-black/10 p-2">
          <div className="text-[9px] text-black/50">{t('mockups.fan.price')}</div>
          <div className="text-sm font-black text-black">$34.900</div>
        </div>
        <div className="rounded-lg bg-yellow-50 border border-yellow-400/40 p-2">
          <div className="text-[9px] text-yellow-800 font-semibold">{t('mockups.fan.withVisaCoupon')}</div>
          <div className="text-sm font-black text-yellow-700">$27.900</div>
        </div>
      </div>
      <div className="flex items-center gap-1 mb-3">
        {['Original', 'Vintage', 'Champions', 'Premium'].map((s) => (
          <div key={s} className={`flex-1 h-7 rounded border text-[9px] font-bold flex items-center justify-center ${s === 'Champions' ? 'bg-slate-900 text-white border-slate-900' : 'text-black/70 border-black/15'}`}>{s}</div>
        ))}
      </div>
      <div className="h-9 rounded-lg bg-gradient-to-r from-sky-500 to-blue-600 flex items-center justify-center text-white text-[11px] font-bold shadow-lg">
        {t('mockups.fan.addToCart')}
      </div>
    </div>
  );
}


const sports = [
  { icon: Trophy, name: 'Fútbol', desc: 'Locales, visitantes, playoffs y finales' },
  { icon: Volleyball, name: 'Rugby', desc: 'Test matches, torneos y provinciales' },
  { icon: Star, name: 'Tenis', desc: 'ATP, WTA, Copa Davis, exhibiciones' },
  { icon: Dumbbell, name: 'Básquet', desc: 'Liga Nacional, playoffs y selección' },
  { icon: Gamepad2, name: 'eSports', desc: 'Torneos, LAN parties y masters' },
  { icon: Bike, name: 'Motor / Ciclismo', desc: 'Fórmulas, MotoGP, vueltas' },
];

export default function FanExperience() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const to = (p) => localizePath(p, lang);
  const howSteps = t('services.fanExperience.howItWorks.steps', { returnObjects: true }) || [];
  const merchItems = t('services.fanExperience.merch.items', { returnObjects: true }) || [];
  const sponsorItems = t('services.fanExperience.sponsors.items', { returnObjects: true }) || [];
  const measureTags = t('services.fanExperience.measure.tags', { returnObjects: true }) || [];
  return (
    <>
      <SEO
        title={t('services.fanExperience.seoTitle')}
        description={t('services.fanExperience.seoDescription')}
        path="/servicios/fan-experience"
        schema={serviceSchema({ name: 'Fan Experience con IA para Clubes y Ligas', description: 'Álbumes personalizados de fotos para hinchas con reconocimiento facial, integración de sponsors y CRM de fans.', path: '/servicios/fan-experience' })}
      />
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 lg:pt-20 pb-16">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 -left-32 w-[500px] h-[500px] bg-cyan-500/12 rounded-full blur-[140px]" />
          <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-violet-500/12 rounded-full blur-[160px]" />
        </div>
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Link to={to('/')} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-violet-300 mb-5 hover:text-violet-200 transition-colors">
                <span>{t('services.fanExperience.breadcrumb')}</span><span>/</span><span className="text-white/60">{t('services.fanExperience.breadcrumbCurrent')}</span>
              </Link>
              <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.02]">
                {t('services.fanExperience.heroTitle1')}
                <span className="block bg-gradient-to-r from-cyan-300 via-sky-300 to-violet-300 bg-clip-text text-transparent">
                  {t('services.fanExperience.heroTitleHighlight')}
                </span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
                {t('services.fanExperience.heroSubtitle')}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to={to('/request-demo')}>
                  <Button className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-6 rounded-full font-semibold">
                    {t('services.fanExperience.ctaDemo')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to={to('/precios')}>
                  <Button variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    {t('services.fanExperience.ctaPricing')}
                  </Button>
                </Link>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
                <div>
                  <div className="font-display text-2xl font-bold text-white">6.4x</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.fanExperience.heroStats.views')}</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-white">83%</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.fanExperience.heroStats.share')}</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-white">18d</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.fanExperience.heroStats.lifetime')}</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <MatchEventCard />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 lg:py-24 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3">{t('services.fanExperience.howItWorks.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.fanExperience.howItWorks.title')}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { n: 1, img: 'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/s5buxgya_MOCKUP%20CELULAR%20FAN%20PUMAS%20%281%29.webp', color: 'bg-cyan-500' },
              { n: 2, img: 'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/nj3x8hnl_MOCKUP%20CELULAR%20FAN%20PUMAS%20%282%29.webp', color: 'bg-violet-500' },
              { n: 3, img: 'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/4ub9gqr7_MOCKUP%20CELULAR%20FAN%20PUMAS%20%283%29.webp', color: 'bg-amber-500' },
            ].map((s, i) => (
              <div key={s.n} className="flex flex-col items-center text-center">
                <div className="relative w-full max-w-[280px]">
                  <img
                    src={s.img}
                    alt={howSteps[i]?.title}
                    className="w-full h-auto object-contain drop-shadow-[0_25px_50px_rgba(139,92,246,0.28)]"
                    loading="lazy"
                  />
                </div>
                <div className={`mt-6 w-8 h-8 rounded-full ${s.color} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                  {s.n}
                </div>
                <h3 className="font-display text-lg md:text-xl font-semibold mt-4 mb-2">{howSteps[i]?.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed max-w-xs">{howSteps[i]?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MERCH + SPONSOR EXTRAS */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3 flex items-center gap-2">
                <Ticket className="w-3.5 h-3.5" /> {t('services.fanExperience.merch.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.fanExperience.merch.titleLine1')}
                <br />
                {t('services.fanExperience.merch.titleLine2Prefix')} <span className="bg-gradient-to-r from-cyan-300 to-fuchsia-300 bg-clip-text text-transparent">{t('services.fanExperience.merch.titleHighlight')}</span>
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.fanExperience.merch.description')}
              </p>

              <div className="mt-8 grid grid-cols-2 gap-3">
                {[Ticket, Award, Camera, Zap, QrCode, Users].map((Icon, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-[#111116] border border-white/8">
                    <div className="w-9 h-9 rounded-lg bg-violet-500/10 border border-violet-400/25 flex items-center justify-center text-violet-300 shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">{merchItems[i]?.title}</div>
                      <div className="text-xs text-white/60 mt-0.5">{merchItems[i]?.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <MerchCheckoutMockup />
              {/* Floating cupon card */}
              <div className="absolute -top-4 -right-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 p-4 text-white shadow-2xl max-w-[220px] rotate-3">
                <div className="text-[9px] uppercase tracking-wider opacity-80">{t('mockups.fan.sponsorCoupon')}</div>
                <div className="text-sm font-black leading-tight">{t('mockups.fan.couponBodyMerch')}</div>
                <div className="text-[9px] mt-1 opacity-90">{t('mockups.fan.couponCodeMerch')}</div>
              </div>
              {/* Floating exp card */}
              <div className="absolute -bottom-4 -left-4 rounded-2xl bg-slate-900 border border-white/15 p-3 shadow-2xl max-w-[200px] -rotate-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-slate-900">
                    <Trophy className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-white leading-tight">{t('mockups.fan.meetGreet')}</div>
                    <div className="text-[9px] text-white/60">{t('mockups.fan.meetGreetSub')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPONSOR ECOSYSTEM */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3 flex items-center gap-2">
                <Award className="w-3.5 h-3.5" /> {t('services.fanExperience.sponsors.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.fanExperience.sponsors.titleLine1')} <span className="bg-gradient-to-r from-cyan-300 to-fuchsia-300 bg-clip-text text-transparent">{t('services.fanExperience.sponsors.titleHighlight')}</span>
                <br />
                {t('services.fanExperience.sponsors.titleLine2')}
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.fanExperience.sponsors.description')}
              </p>
              <ul className="mt-8 space-y-3">
                {[Sparkles, Ticket, QrCode, Radio].map((Icon, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/80">
                    <div className="w-8 h-8 rounded-lg bg-violet-500/10 border border-violet-400/25 flex items-center justify-center text-violet-300 shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-sm leading-relaxed pt-1">{sponsorItems[i]}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl bg-[#08080c] border border-white/8 p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="text-sm font-semibold text-white">{t('services.fanExperience.sponsors.dashboardTitle')}</div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider">{t('services.fanExperience.sponsors.dashboardActive')}</div>
              </div>
              <FanSponsorTiers />
              <div className="mt-6 pt-6 border-t border-white/8 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="font-display text-xl font-bold text-white">2.1M</div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">{t('services.fanExperience.sponsors.dashboardImpressions')}</div>
                </div>
                <div>
                  <div className="font-display text-xl font-bold text-white">86k</div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">{t('services.fanExperience.sponsors.dashboardClicks')}</div>
                </div>
                <div>
                  <div className="font-display text-xl font-bold text-emerald-300">4.1x ROI</div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">{t('services.fanExperience.sponsors.dashboardReturn')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEASURE SUCCESS — Statistics dashboard */}
      <section className="py-24 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-500/18 via-cyan-500/12 to-rose-500/18 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-sky-500/20 rounded-full blur-[160px]" />
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/70 mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> {t('services.fanExperience.measure.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight text-white">
                {t('services.fanExperience.measure.titleLine1')}
                <br />
                {t('services.fanExperience.measure.titleLine2')}
              </h2>
              <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-xl">
                {t('services.fanExperience.measure.description')}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {measureTags.map((tag) => (
                  <span key={tag} className="px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-sm text-white">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <StatsDashboard
                config={{
                  accent: {
                    chartFrom: 'from-sky-400',
                    chartVia: 'via-cyan-400',
                    chartTo: 'to-rose-400',
                    dayPillBg: 'bg-sky-500',
                  },
                  browserUrl: 'dashboard.momentia.shop / Racing vs Independiente',
                  period: 'Durante el partido · en vivo',
                  kpis: [
                    { icon: '🎫', label: 'Hinchas activos', value: '38.5k', sub: '92% del estadio', color: 'text-sky-600' },
                    { icon: '📸', label: 'Fotos por hincha', value: '14', sub: '512k entregadas', color: 'text-cyan-600' },
                    { icon: '🔥', label: 'Engagement', value: '46%', sub: '89k shares · 6.7k al CRM', color: 'text-rose-600' },
                  ],
                  chart: {
                    title: 'Álbumes abiertos por minuto',
                    bars: [24, 42, 55, 68, 45, 88, 52, 74, 92, 96, 82, 68, 44],
                  },
                  sponsorRow: {
                    title: 'Reporte de sponsors',
                    subtitle: 'Visa · Imperial · DirecTV',
                    items: [
                      { icon: '👁', label: 'Impresiones', value: '2.1M' },
                      { icon: '🔗', label: 'Clicks a links', value: '186k' },
                      { icon: '🎟️', label: 'Cupones', value: '3.242' },
                    ],
                  },
                  badges: [
                    { emoji: '⚽', text: '¡GOL!', pos: 'tl' },
                    { emoji: '🔥', text: '+284 shares', pos: 'br' },
                    { emoji: '👥', text: '+142 al CRM', pos: 'bml' },
                  ],
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-mi">
          <div className="relative overflow-hidden rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-[#0e1a2a] via-[#101018] to-[#0e0e14] p-10 md:p-14 text-center">
            <div className="absolute -top-32 -right-24 w-[500px] h-[500px] bg-cyan-500/15 rounded-full blur-[100px]" />
            <div className="absolute -bottom-32 -left-24 w-[500px] h-[500px] bg-violet-500/15 rounded-full blur-[100px]" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-cyan-400/25 bg-cyan-500/10 text-xs text-cyan-200 mb-6">
                <Zap className="w-3 h-3" /> {t('services.fanExperience.finalCta.badge')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-white">
                {t('services.fanExperience.finalCta.title')}
              </h2>
              <p className="mt-4 text-white/70 max-w-xl mx-auto">
                {t('services.fanExperience.finalCta.subtitle')}
              </p>
              <div className="mt-8 flex justify-center gap-3 flex-wrap">
                <Link to={to('/request-demo')}>
                  <Button className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-7 rounded-full font-semibold">
                    {t('services.fanExperience.finalCta.ctaDemo')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to={to('/precios')}>
                  <Button variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    {t('services.fanExperience.finalCta.ctaPricing')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
